/**
 * Utility functions for working with cron expressions
 * Converts technical cron format to human-readable descriptions
 */

/**
 * Convert a cron expression to human-readable text
 * @param {string} cronExpression - The cron expression (e.g., '0 2 * * *')
 * @returns {string} Human-readable description
 */
export function cronToHumanReadable(cronExpression) {
  if (!cronExpression || typeof cronExpression !== 'string') {
    return 'Not configured';
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return cronExpression; // Return as-is if invalid format
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Check for common patterns first
  
  // Daily patterns
  if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
    if (hour !== '*' && !hour.includes('/')) {
      const time = formatHourList(hour, minute);
      return `Every day at ${time}`;
    }
  }

  // Weekdays (Mon-Fri)
  if (dayOfWeek === '1-5' && dayOfMonth === '*' && month === '*') {
    if (hour !== '*' && !hour.includes('/')) {
      const time = formatHourList(hour, minute);
      return `Weekdays at ${time}`;
    }
  }

  // Weekends
  if ((dayOfWeek === '0,6' || dayOfWeek === '6,0') && dayOfMonth === '*' && month === '*') {
    if (hour !== '*' && !hour.includes('/')) {
      const time = formatHourList(hour, minute);
      return `Weekends at ${time}`;
    }
  }

  // Specific days of week
  if (dayOfWeek !== '*' && dayOfMonth === '*' && month === '*' && hour !== '*' && !hour.includes('/')) {
    const days = parseDaysOfWeek(dayOfWeek);
    const time = formatHourList(hour, minute);
    
    if (days.length === 1) {
      return `Every ${days[0]} at ${time}`;
    } else if (days.length === 7) {
      return `Every day at ${time}`;
    } else {
      return `${days.join(', ')} at ${time}`;
    }
  }

  // Hourly patterns
  if (hour.startsWith('*/')) {
    const interval = parseInt(hour.substring(2));
    const min = parseInt(minute) || 0;
    
    if (interval === 1) {
      return `Every hour at :${min.toString().padStart(2, '0')}`;
    } else {
      return `Every ${interval} hours at :${min.toString().padStart(2, '0')}`;
    }
  }

  // Every hour
  if (hour === '*' && minute !== '*') {
    const min = parseInt(minute) || 0;
    return `Every hour at :${min.toString().padStart(2, '0')}`;
  }

  // Every minute (unlikely but possible)
  if (hour === '*' && minute === '*') {
    return 'Every minute';
  }

  // If we couldn't parse it, return a generic description
  return buildGenericDescription(minute, hour, dayOfMonth, month, dayOfWeek);
}

/**
 * Format hour and minute to 12-hour time
 * @param {number} hour24 - Hour in 24-hour format (0-23)
 * @param {number} minute - Minute (0-59)
 * @returns {string} Formatted time (e.g., "2:00 AM")
 */
function formatTime(hour24, minute) {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const minuteStr = minute.toString().padStart(2, '0');
  return `${hour12}:${minuteStr} ${period}`;
}

// Render an hour spec ("4" or "4,11,18") + minute into a human time list.
// Caller is expected to have already excluded "*" and "*/N" hour forms.
function formatHourList(hourSpec, minuteSpec) {
  const min = parseInt(minuteSpec) || 0;
  if (typeof hourSpec === 'string' && hourSpec.includes(',')) {
    return hourSpec
      .split(',')
      .map(h => formatTime(parseInt(h), min))
      .join(', ');
  }
  return formatTime(parseInt(hourSpec), min);
}

/**
 * Parse day of week field to day names
 * @param {string} dayOfWeek - Day of week field from cron
 * @returns {Array<string>} Array of day names
 */
function parseDaysOfWeek(dayOfWeek) {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const days = [];

  // Handle ranges (e.g., "1-5")
  if (dayOfWeek.includes('-')) {
    const [start, end] = dayOfWeek.split('-').map(d => parseInt(d));
    for (let i = start; i <= end; i++) {
      days.push(dayNames[i]);
    }
    return days;
  }

  // Handle comma-separated values (e.g., "1,3,5")
  if (dayOfWeek.includes(',')) {
    const dayNumbers = dayOfWeek.split(',').map(d => parseInt(d));
    return dayNumbers.map(n => dayNames[n]);
  }

  // Single day
  const dayNum = parseInt(dayOfWeek);
  if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
    return [dayNames[dayNum]];
  }

  return [];
}

/**
 * Build a generic description when specific patterns don't match
 * @returns {string} Generic description
 */
function buildGenericDescription(minute, hour, dayOfMonth, month, dayOfWeek) {
  const parts = [];

  // Frequency
  if (minute === '*' && hour === '*') {
    return 'Every minute';
  }
  
  if (hour === '*') {
    parts.push('Hourly');
  } else if (hour.includes('/')) {
    const interval = hour.split('/')[1];
    parts.push(`Every ${interval} hours`);
  } else if (dayOfWeek === '*' && dayOfMonth === '*') {
    parts.push('Daily');
  } else if (dayOfWeek !== '*') {
    parts.push('Weekly');
  } else if (dayOfMonth !== '*') {
    parts.push('Monthly');
  }

  // Time
  if (hour !== '*' && !hour.includes('/')) {
    parts.push(`at ${formatHourList(hour, minute)}`);
  } else if (minute !== '*') {
    const m = parseInt(minute) || 0;
    parts.push(`at :${m.toString().padStart(2, '0')}`);
  }

  return parts.join(' ') || 'Custom schedule';
}

/**
 * Get a short description suitable for badges or compact displays
 * @param {string} cronExpression - The cron expression
 * @returns {string} Short description
 */
export function cronToShortDescription(cronExpression) {
  if (!cronExpression || typeof cronExpression !== 'string') {
    return 'Not set';
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return 'Custom';
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Hourly
  if (hour.startsWith('*/')) {
    const interval = parseInt(hour.substring(2));
    return `Every ${interval}h`;
  }

  if (hour === '*') {
    return 'Hourly';
  }

  // Daily
  if (dayOfWeek === '*' && dayOfMonth === '*') {
    return 'Daily';
  }

  // Weekdays
  if (dayOfWeek === '1-5') {
    return 'Weekdays';
  }

  // Weekends
  if (dayOfWeek === '0,6' || dayOfWeek === '6,0') {
    return 'Weekends';
  }

  // Weekly
  if (dayOfWeek !== '*' && dayOfWeek.includes(',')) {
    return 'Weekly';
  }

  if (dayOfWeek !== '*') {
    return 'Weekly';
  }

  return 'Custom';
}
